/**
 * Production personal work assistant portal (Northstar Desk).
 * Intent → compose → draft / agenda / triage with copy + packet download.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import TodayIcon from '@mui/icons-material/Today';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { adviseDemoAssistantApi, getPersonalCatalogApi } from '../../api/demoAssistants.api';
import ClawDecisionPanel from '../../components/demo/ClawDecisionPanel';
import DemoDictateTextField from '../../components/demo/DemoDictateTextField';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';

const INK = '#0f172a';
const NAVY = '#1e3a5f';
const ACCENT = '#c45c26';

const INTENT_ICONS = {
  email: MailOutlineIcon,
  meeting: EventNoteIcon,
  tasks: ChecklistRtlIcon,
  'day-brief': TodayIcon,
};

function copyText(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return Promise.reject(new Error('Clipboard unavailable'));
}

function downloadPacket(packet) {
  const blob = new Blob([JSON.stringify(packet, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `northstar-packet-${packet?.intent || 'assist'}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function OutcomeCard({ result, onCopied }) {
  const draft = result?.draft?.content || result?.brief?.content || '';
  const intent = result?.routing?.primaryTrack || 'email';
  const meeting = result?.meetingPrep;
  const tasks = result?.tasks?.rows || result?.checklist?.rows || [];
  const { speak, cancel, isSpeaking, hasSupport } = useSpeechSynthesis();
  const speakText = [
    result?.draft?.subject || result?.form_prefill?.topic,
    result?.routing?.rationale,
    draft,
  ].filter(Boolean).join('. ').slice(0, 1200);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(30,58,95,0.18)',
        bgcolor: '#fff',
        p: { xs: 2, sm: 2.75 },
        background: 'linear-gradient(165deg, rgba(30,58,95,0.06) 0%, #fff 42%)',
      }}
      data-testid="personal-outcome-card"
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} alignItems="center">
        <Chip
          label={String(intent).replace(/-/g, ' ')}
          size="small"
          sx={{ bgcolor: NAVY, color: '#fff', fontWeight: 600, textTransform: 'capitalize' }}
        />
        <Chip
          label={result?.routing?.rationale || ''}
          size="small"
          variant="outlined"
          sx={{ borderColor: 'rgba(30,58,95,0.25)' }}
        />
        {hasSupport && (
          <Button
            size="small"
            startIcon={<VolumeUpIcon />}
            onClick={() => (isSpeaking ? cancel() : speak(speakText))}
            sx={{ textTransform: 'none', color: NAVY }}
          >
            {isSpeaking ? 'Stop' : 'Listen'}
          </Button>
        )}
      </Stack>

      <Typography
        sx={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 650,
          fontSize: { xs: '1.35rem', sm: '1.5rem' },
          color: INK,
          letterSpacing: '-0.02em',
          mb: 1.5,
        }}
      >
        {result?.draft?.subject || result?.form_prefill?.topic || 'Assistant output'}
      </Typography>

      {meeting && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: NAVY, mb: 0.75 }}>Agenda</Typography>
          <Stack component="ol" sx={{ m: 0, pl: 2.25 }} spacing={0.35}>
            {(meeting.agenda || []).map((a) => (
              <Typography component="li" key={a} variant="body2" color="text.secondary">{a}</Typography>
            ))}
          </Stack>
          <Typography variant="subtitle2" sx={{ color: NAVY, mt: 1.5, mb: 0.75 }}>Talking points</Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2.25 }} spacing={0.35}>
            {(meeting.talkingPoints || []).map((t) => (
              <Typography component="li" key={t} variant="body2" color="text.secondary">{t}</Typography>
            ))}
          </Stack>
        </Box>
      )}

      {tasks.length > 0 && intent !== 'email' && (
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {tasks.map((t) => (
            <Box
              key={t.id}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'baseline',
                bgcolor: 'rgba(30,58,95,0.04)',
                borderRadius: 2,
                px: 1.25,
                py: 0.85,
              }}
            >
              <Chip label={t.priority} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{t.label}</Typography>
              <Typography variant="caption" color="text.secondary">{t.block}</Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Box
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '0.9rem',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          bgcolor: 'rgba(15,23,42,0.03)',
          border: '1px solid rgba(15,23,42,0.08)',
          borderRadius: 2,
          p: 2,
          mb: 2,
          color: INK,
        }}
      >
        {draft}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          onClick={async () => {
            try {
              await copyText(draft);
              onCopied?.('Copied to clipboard');
            } catch (_) {
              onCopied?.('Copy failed — select text manually');
            }
          }}
          sx={{
            textTransform: 'none',
            bgcolor: NAVY,
            borderRadius: 2,
            fontWeight: 600,
            '&:hover': { bgcolor: '#16324f' },
          }}
          data-testid="personal-copy-draft"
        >
          Copy output
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => downloadPacket(result?.handoffPacket || { intent, draft })}
          sx={{ textTransform: 'none', borderRadius: 2, borderColor: NAVY, color: NAVY }}
        >
          Download packet
        </Button>
      </Stack>

      <ClawDecisionPanel
        claw={result?.claw}
        handoffPacket={result?.handoffPacket}
        accent={NAVY}
        defaultOpen
      />
    </Box>
  );
}

export default function PersonalAssistantPortalPage() {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [step, setStep] = useState(0);
  const [intent, setIntent] = useState('email');
  const [role, setRole] = useState('manager');
  const [tone, setTone] = useState('professional');
  const [recipient, setRecipient] = useState('');
  const [topic, setTopic] = useState('');
  const [context, setContext] = useState('');
  const [scenarioId, setScenarioId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPersonalCatalogApi();
        if (!cancelled) setCatalog(res?.catalog || null);
      } catch (err) {
        if (!cancelled) setCatalogError(err?.message || err?.error || 'Catalog offline');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const brand = catalog?.brand || { name: 'Northstar Desk', tagline: 'Personal work assistant', primaryColor: NAVY };
  const intents = catalog?.intents || [
    { id: 'email', label: 'Email draft', blurb: 'Draft a message' },
    { id: 'meeting', label: 'Meeting prep', blurb: 'Agenda & questions' },
    { id: 'tasks', label: 'Task triage', blurb: 'Prioritize a list' },
    { id: 'day-brief', label: 'Day brief', blurb: 'Focus for today' },
  ];
  const scenarios = catalog?.scenarios || [];

  const pickScenario = useCallback((sc) => {
    setScenarioId(sc.id);
    setIntent(sc.intent || 'email');
    setRole(sc.role || 'manager');
    setTone(sc.tone || 'professional');
    setRecipient(sc.recipient || '');
    setTopic(sc.topic || '');
    setContext(sc.context || '');
    setResult(null);
    setStep(1);
    setError(null);
  }, []);

  const submit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await adviseDemoAssistantApi('personal', {
        scenarioId: scenarioId || undefined,
        intent,
        role,
        tone,
        recipient,
        topic,
        context,
      });
      const payload = raw?.result ? raw : raw?.data || raw;
      const adviseResult = payload?.result || payload;
      if (!adviseResult?.draft && !adviseResult?.routing) {
        throw new Error(payload?.error || 'Unexpected advise response');
      }
      setResult(adviseResult);
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Advise failed');
    } finally {
      setLoading(false);
    }
  }, [scenarioId, intent, role, tone, recipient, topic, context]);

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#f5f6f8',
        backgroundImage:
          'radial-gradient(ellipse 70% 45% at 0% 0%, rgba(30,58,95,0.12), transparent),'
          + 'radial-gradient(ellipse 50% 35% at 100% 0%, rgba(196,92,38,0.08), transparent)',
      }}
      data-testid="personal-portal"
    >
      <Box
        component="header"
        sx={{
          borderBottom: '1px solid rgba(15,23,42,0.08)',
          bgcolor: 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(10px)',
          position: 'sticky',
          top: 0,
          zIndex: 4,
        }}
      >
        <Box sx={{
          maxWidth: 920,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 1.75,
        }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 700,
              fontSize: { xs: '1.45rem', sm: '1.7rem' },
              letterSpacing: '-0.03em',
              color: brand.primaryColor || NAVY,
              lineHeight: 1.1,
            }}
          >
            {brand.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {brand.tagline || 'Personal work assistant'}
            {' · '}
            <Link component={RouterLink} to="/studio/help/personal-assistant" underline="hover" color="inherit">
              How this works
            </Link>
            {' · '}
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              InterviewFlow
            </Link>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 920, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 4 } }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['Choose intent', 'Compose', 'Output'].map((label, i) => (
              <Chip
                key={label}
                label={`${i + 1}. ${label}`}
                size="small"
                onClick={() => { if (i <= step) setStep(i); }}
                sx={{
                  fontWeight: step === i ? 700 : 500,
                  bgcolor: step === i ? NAVY : step > i ? 'rgba(30,58,95,0.12)' : 'rgba(0,0,0,0.04)',
                  color: step === i ? '#fff' : INK,
                  cursor: i <= step ? 'pointer' : 'default',
                }}
              />
            ))}
          </Stack>

          {catalogError && <Alert severity="warning">Catalog offline — free-form compose still works. ({catalogError})</Alert>}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {toast && <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>}

          {step === 0 && (
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)', p: { xs: 2, sm: 3 } }}>
              <Typography sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 650, fontSize: '1.4rem', color: INK, mb: 0.5 }}>
                What do you need?
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2.5, maxWidth: 480 }}>
                Pick an intent or jump in with a demo scenario — email, meeting prep, triage, or day brief.
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
                {intents.map((it) => {
                  const Icon = INTENT_ICONS[it.id] || MailOutlineIcon;
                  const active = intent === it.id;
                  return (
                    <Button
                      key={it.id}
                      onClick={() => { setIntent(it.id); setScenarioId(''); setStep(1); }}
                      variant={active ? 'contained' : 'outlined'}
                      startIcon={<Icon sx={{ fontSize: 18 }} />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        ...(active
                          ? { bgcolor: NAVY, '&:hover': { bgcolor: '#16324f' } }
                          : { borderColor: 'rgba(30,58,95,0.3)', color: NAVY }),
                      }}
                    >
                      {it.label}
                    </Button>
                  );
                })}
              </Stack>

              {scenarios.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Try a demo scenario
                  </Typography>
                  <Stack spacing={1}>
                    {scenarios.map((sc) => (
                      <Button
                        key={sc.id}
                        onClick={() => pickScenario(sc)}
                        variant="outlined"
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          borderRadius: 2,
                          borderColor: 'rgba(30,58,95,0.22)',
                          color: INK,
                          py: 1.25,
                          textAlign: 'left',
                        }}
                      >
                        <Box>
                          <Typography fontWeight={600}>{sc.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sc.intent} · {sc.role} · {sc.topic}
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}

          {step === 1 && (
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)', p: { xs: 2, sm: 3 } }}>
              <Typography sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 650, fontSize: '1.35rem', color: INK, mb: 2 }}>
                Compose
              </Typography>
              <Stack spacing={1.75} sx={{ maxWidth: 520 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Intent</InputLabel>
                  <Select label="Intent" value={intent} onChange={(e) => setIntent(e.target.value)}>
                    {intents.map((it) => (
                      <MenuItem key={it.id} value={it.id}>{it.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                      <MenuItem value="executive">Executive</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                      <MenuItem value="ic">IC</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Tone</InputLabel>
                    <Select label="Tone" value={tone} onChange={(e) => setTone(e.target.value)}>
                      <MenuItem value="concise">Concise</MenuItem>
                      <MenuItem value="professional">Professional</MenuItem>
                      <MenuItem value="warm">Warm</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <TextField
                  label="Recipient / audience"
                  size="small"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Subject / focus"
                  size="small"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  fullWidth
                  required
                />
                <DemoDictateTextField
                  label="Context / notes / task list"
                  size="small"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  helperText="Tap the mic to dictate context"
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                <Button onClick={() => setStep(0)} sx={{ textTransform: 'none', color: NAVY }}>Back</Button>
                <Button
                  variant="contained"
                  disabled={loading || !topic.trim()}
                  onClick={submit}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{
                    textTransform: 'none',
                    bgcolor: NAVY,
                    borderRadius: 2,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#16324f' },
                  }}
                  data-testid="personal-generate"
                >
                  Generate
                </Button>
              </Stack>
            </Box>
          )}

          {step === 2 && result && (
            <OutcomeCard result={result} onCopied={setToast} />
          )}
        </Stack>
      </Box>
    </Box>
  );
}
